import { yupResolver } from '@hookform/resolvers/yup';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { FormGroup } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import AvatarInput from 'v2/form/AvatarInput/AvatarInput';
import Input from 'v2/form/Input/Input';
import { GenericObject, User, Permission, Role } from 'v2/models';
import * as yup from 'yup';
import { updateUserProfileAction } from '../../../../actions';
import styles from '../settingsForm.module.scss';
import { getUserRole } from 'v2/utils/user';
import { EditSubmitButton } from 'v2/common/EditSubmitButton/EditSubmitButton';
import {
  NotificationSettingsForm,
  NotificationSettingsSchema,
  TNotificationSettingsFormValues,
} from 'v2/common/NotificaitonSettingsForm/NotificationSettingsForm';
import { getNotificationEditPerms } from 'v2/common/NotificaitonSettingsForm/getNotificationEditPerms';
import {
  getNotificationSettings,
  getNotificationSettingsFormValues,
} from 'v2/common/NotificaitonSettingsForm/getNotificationSettings';
import BccEmail from './BccEmail/BccEmail';
import { hasPermission, PERMISSION_NAME } from 'v2/utils/permissions';

const schema = yup.object().shape({
  email: yup.string(),
  firstName: yup.string().required('Required'),
  lastName: yup.string().required('Required'),
  jobTitle: yup.string().required('Required'),
  notifySettings: NotificationSettingsSchema,
});

interface DispatchProps {
  // Need to use Funtion type here because coulnd't find the proper type definition for
  // updateUserProfileAction action and make it work with the connect redux function
  // eslint-disable-next-line @typescript-eslint/ban-types
  updateUserProfileAction: Function;
}

interface StateProps {
  data: {
    avatar: string | File;
    email: string;
    firstName: string;
    lastName: string;
    jobTitle: string;
    notifySettings: TNotificationSettingsFormValues;
    role: Role;
  };
  permissions: { [key: string]: Permission };
}

type MyAccountProps = StateProps & DispatchProps;

const MyAccount = ({
  data,
  permissions,
  updateUserProfileAction,
}: MyAccountProps) => {
  const formProps = useForm<StateProps['data']>({
    defaultValues: data || {
      avatar: '',
      email: '',
      firstName: '',
      lastName: '',
      jobTitle: '',
      notifySettings: {
        emailOrg: 'no',
        emailUser: 'no',
        emailNewForReviewSearches: 'no',
        emailNewInvoice: 'no',
        emailNewProviderResponseForReviewedRequests: 'no',
      },
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    register,
    formState: { errors, isDirty },
    setValue,
    getValues,
    reset,
  } = formProps;

  const [isEditing, setIsEditing] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const formValues = getValues();
  const hasDrmPermission = hasPermission(permissions, [PERMISSION_NAME.DRM]);

  useEffect(() => {
    // Need to register the Avatar field here because Avatar is not a common form field
    register('avatar');
  }, [register]);

  const onSubmit = async (values: StateProps['data']) => {
    try {
      const dataToSave = {
        first_name: values.firstName,
        last_name: values.lastName,
        job_title: values.jobTitle,
        notification_settings: getNotificationSettings(values.notifySettings),
        avatar: values.avatar,
      };
      setIsBusy(true);
      if (isDirty) {
        const response = await updateUserProfileAction(dataToSave);
        reset(mapUserSettings(response));
        toast.success('Account updated.');
      }
      setIsEditing(false);
      setIsBusy(false);
    } catch (error) {
      toast.error(String(error));
    }
  };

  const notificationPermissions = getNotificationEditPerms(
    data.role,
    permissions
  );

  return (
    <FormProvider {...formProps}>
      <h4 className={classnames('mb-5', styles.title)}>Account Information</h4>
      <form onSubmit={handleSubmit(onSubmit)}>
        <AvatarInput
          className={classnames('form-group', styles.avatar)}
          file={formValues.avatar || ''}
          initials={`${data.firstName?.[0]}${data.lastName?.[0]}`}
          label="Profile Photo"
          onChange={(data) => {
            setValue('avatar', data || '', {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
          readonly={!isEditing}
          changeBtnText="Edit"
        />
        {hasDrmPermission && <BccEmail />}
        <div className={classnames(styles.formGrid, 'mt-5 mb-4')}>
          <FormGroup>
            <Input
              label="Email Address"
              type="text"
              register={register('email')}
              required={false}
              disabled={true}
            />
          </FormGroup>
          <FormGroup>
            <Input
              label="First Name"
              type="text"
              register={register('firstName', { required: true })}
              error={errors?.firstName?.message}
              maxLength={64}
              autoComplete="off"
              required={true}
              disabled={!isEditing}
            />
          </FormGroup>
          <FormGroup>
            <Input
              label="Last Name"
              type="text"
              register={register('lastName', { required: true })}
              error={errors?.lastName?.message}
              maxLength={64}
              autoComplete="off"
              required={true}
              disabled={!isEditing}
            />
          </FormGroup>
          <FormGroup>
            <Input
              label="Job Title"
              type="text"
              register={register('jobTitle', { required: true })}
              error={errors?.jobTitle?.message}
              maxLength={64}
              autoComplete="off"
              required={true}
              disabled={!isEditing}
            />
          </FormGroup>
        </div>

        <NotificationSettingsForm
          namespacePrefix="notifySettings"
          disabled={!isEditing}
          showEmailOrg={notificationPermissions.canEditEmailOrg}
          showEmailUser={notificationPermissions.canEditEmailUser}
          showEmailNewForReviewSearches={
            notificationPermissions.canEditNewForReviewSearches
          }
          showEmailNewProviderResponseForReviewedRequests={
            notificationPermissions.canEditNewProviderResponseForReviewedRequests
          }
          showEmailNewInvoice={notificationPermissions.canEditNewInvoice}
          showWebNotifications={true}
        />

        <EditSubmitButton
          isBusy={isBusy}
          isEditing={isEditing}
          label="Edit details"
          onEdit={() => {
            setIsEditing(true);
          }}
        />
      </form>
    </FormProvider>
  );
};

const mapStateToProps = (state: GenericObject): StateProps => {
  return {
    data: mapUserSettings(state.user.user),
    permissions: state.permissions.permissions,
  };
};

export default connect<StateProps, DispatchProps>(mapStateToProps, {
  updateUserProfileAction,
})(MyAccount);

function mapUserSettings(user: User): StateProps['data'] {
  return {
    email: user.email,
    firstName: user.first_name || '',
    lastName: user.last_name || '',
    jobTitle: user.job_title || '',
    notifySettings: getNotificationSettingsFormValues(
      user.notification_settings
    ),
    avatar: user?.avatar?.formats?.thumbnail?.url || user?.avatar?.url || '',
    role: getUserRole(user),
  };
}
